import React, { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileSignature, Download, ShieldCheck, AlertTriangle } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/components/bp/bpLabels";

const REASONS = { initial: "Première signature", new_version: "Nouvelle version du modèle de contrat", grid_changed: "Votre grille de commission a changé" };

export default function BpContrat() {
  const { refresh } = useOutletContext();
  const [data, setData] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/bp/contract").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    setBusy(true);
    try {
      const { data: c } = await api.post("/bp/contract/sign", { accepted });
      toast.success(`${c.kind === "avenant" ? "Avenant" : "Contrat"} ${c.reference} signé — PDF disponible`);
      setAccepted(false); await load(); refresh?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const openPdf = async (c) => {
    try { const r = await api.get(`/bp/contract/${c.id}/pdf`, { responseType: "blob" }); const url = URL.createObjectURL(r.data); window.open(url, "_blank", "noopener"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("PDF indisponible"); }
  };

  if (!data) return <div className="p-10 text-sm text-[#868E96]" data-testid="bp-contract-loading">Chargement du contrat…</div>;
  const cur = data.current;
  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-5xl" data-testid="bp-contract">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-bold mb-2">// Mon contrat</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Contrat Business Partner</h1>
        <p className="text-sm text-[#495057] mt-2">Personnalisé avec vos informations et votre grille de commission, signé électroniquement en un clic (horodatage, adresse IP, empreinte SHA-256), archivé en PDF.</p>
      </div>

      {cur && !data.needs_signature && (
        <div className="border border-[#A7F3D0] bg-[#ECFDF5] p-4 flex flex-wrap items-center gap-3" data-testid="bp-contract-signed">
          <ShieldCheck className="h-6 w-6 text-[#059669]" />
          <div className="flex-1 text-sm"><b>{cur.kind === "avenant" ? "Avenant" : "Contrat"} {cur.reference}</b> signé le {fmtDateTime(cur.signed_at)} — modèle v{cur.template_version}. Votre contrat est à jour.</div>
          <Button variant="outline" size="sm" onClick={() => openPdf(cur)} className="h-9" data-testid="bp-contract-pdf"><Download className="h-4 w-4 mr-1" />PDF</Button>
        </div>
      )}
      {data.needs_signature && (
        <div className="border-2 border-[#FFD700] bg-[#FFFBEB] p-4 text-sm flex items-start gap-3" data-testid="bp-contract-pending">
          <AlertTriangle className="h-5 w-5 text-[#B45309] shrink-0" />
          <div><b>{data.kind === "avenant" ? "Avenant à signer" : "Contrat à signer"}</b> — {REASONS[data.reason] || data.reason}. {data.kind === "avenant" ? "Vos commissions acquises ne changent pas." : "Nécessaire pour publier votre mini-site et percevoir vos commissions."}</div>
        </div>
      )}

      <article className="bg-white border border-[#DEE2E6] p-6 lg:p-10 legal-doc max-h-[60vh] overflow-y-auto prose prose-sm max-w-none" data-testid="bp-contract-html" dangerouslySetInnerHTML={{ __html: data.html }} />

      {data.needs_signature && (
        <div className="bg-[#0A0A0A] text-white p-6 space-y-4" data-testid="bp-contract-sign-box">
          {data.blockers?.length > 0 && (
            <div className="text-xs text-[#FFD700] space-y-1" data-testid="bp-contract-blockers">
              {data.blockers.map((b) => <div key={b}>• {b} — <Link to={b.startsWith("Acceptez") ? "/bp/reglement" : "/bp/profil"} className="underline font-bold">y aller</Link></div>)}
            </div>
          )}
          <label className={`flex items-start gap-3 text-sm cursor-pointer ${data.can_sign ? "" : "opacity-50"}`}>
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} disabled={!data.can_sign} className="mt-0.5 border-white data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-[#0A0A0A]" data-testid="bp-contract-accept" />
            <span>J'ai lu l'intégralité du {data.kind === "avenant" ? "présent avenant" : "présent contrat"}, j'en accepte les termes et je le signe électroniquement en qualité de représentant de ma structure.</span>
          </label>
          <Button onClick={sign} disabled={!accepted || !data.can_sign || busy} className="bg-[#FFD700] text-[#0A0A0A] hover:bg-[#F0C800] h-11 font-bold" data-testid="bp-contract-sign">
            <FileSignature className="h-4 w-4 mr-2" />{busy ? "Signature…" : data.kind === "avenant" ? "Je signe l'avenant" : "Je signe mon contrat"}
          </Button>
        </div>
      )}

      {data.history?.length > 0 && (
        <section className="bg-white border border-[#DEE2E6]" data-testid="bp-contract-history">
          <div className="px-4 py-3 border-b border-[#DEE2E6] text-[10px] uppercase tracking-[0.25em] font-bold">Documents signés</div>
          <div className="divide-y divide-[#DEE2E6]">
            {data.history.map((c) => (
              <div key={c.id} className="px-4 py-2.5 flex flex-wrap items-center gap-3 text-xs" data-testid={`bp-contract-row-${c.id}`}>
                <span className="font-mono text-[10px] text-[#868E96]">{c.reference}</span>
                <span className="font-semibold">{c.kind === "avenant" ? "Avenant" : "Contrat"} · modèle v{c.template_version}</span>
                <span className="text-[10px] text-[#868E96] flex-1">signé le {fmtDate(c.signed_at)} · {REASONS[c.reason] || ""}</span>
                <Button variant="ghost" size="sm" onClick={() => openPdf(c)} className="h-7" data-testid={`bp-contract-row-pdf-${c.id}`}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
